import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Per-colour copy that follows the selected variant.
 *
 * The text is a variant metafield (custom.color_description), written by
 * Andherson Studio. The variant picker morphs only itself by default, so a block
 * that just reads selected_or_first_available_variant renders once and then goes
 * stale — it never follows the swatch.
 *
 * Like product-price, we take the freshly server-rendered section out of the
 * event's `html` and swap our copy in. That keeps rendering in Liquid: the
 * metafield never has to reach the browser as JSON (the variant payload doesn't
 * carry metafields anyway).
 *
 * @typedef {Object} VariantDescriptionRefs
 * @property {HTMLElement} [copy] - the paragraph holding the text
 *
 * @extends {Component<VariantDescriptionRefs>}
 */
class VariantDescription extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Product cards run their own swatch pickers; don't answer for those.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector(`variant-description[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;

        const newCopy = next.querySelector('[ref="copy"]');

        // Swap our whole contents rather than touching this.refs.
        //
        // refs are refreshed by a MutationObserver that ignores nodes it can no
        // longer resolve to this component — and a node that has just been
        // REMOVED has no parent, so it resolves to nothing. Detach the copy for a
        // colour with no text and refs.copy is stranded pointing at it; the next
        // colour that HAS text then replaceWith()s a detached node, which does
        // nothing at all. That's the "go via an empty colour and it never comes
        // back" bug. replaceChildren runs on `this`, which is always attached.
        //
        // A colour with no copy leaves nothing behind — an empty block under the
        // swatches reads as broken.
        if (!newCopy) {
          this.replaceChildren();
          this.hidden = true;
          return;
        }
        this.replaceChildren(newCopy.cloneNode(true));
        this.hidden = false;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-description] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-description')) {
  customElements.define('variant-description', VariantDescription);
}
